import { _getLogger } from '@doxyme/analytics';
import { Specs, type PostPayload } from '@doxyme/extensions-common';
import { logErrorAndThrowBind, type LogErrorAndThrowBound } from '../../utils';
import type Instance from '../../instance';
import Runtime from '../runtime';

const logger = _getLogger('backendRuntime', {
  component: 'extensions',
  lib: 'bridge',
});
const logErrorAndThrow: LogErrorAndThrowBound = logErrorAndThrowBind(logger);

type Constraints = Specs.Constraints;
type BackendConfig = Specs.Backend.Config;
type PostResponse = Omit<PostPayload, 'runtimeId'>;

const AppTokenUpdate = Specs.AppToken.Events.Update;
const AppTokenGetStatus = Specs.AppToken.Events.GetStatus;

export class BackendRuntime extends Runtime {
  readonly url: URL;

  readonly filter: string[];

  readonly isAuthRequired: boolean | undefined;

  private token: string | undefined;

  eventQueue: PostPayload[] = [];

  constructor(instance: Instance, config: BackendConfig) {
    const backendId = BackendRuntime.runtimeId(instance, config);

    super(backendId, instance);
    this.url = new URL(config.url);
    this.filter = config.filter;
    this.isAuthRequired = config.isAuthRequired;
  }

  async notify(tuple: Constraints['eventPayloadTuple']): Promise<void> {
    const [type, payload] = tuple;

    // AppTokenUpdate is @deprecated, it is here for backwards compatibility, will be removed
    if (type === AppTokenUpdate) {
      this.authenticate(payload); // payload is a token string
      this.emptyQueue();
      return;
    }

    if (type === AppTokenGetStatus) {
      if (payload.isSuccess && payload.data) {
        this.authenticate(payload.data);
        this.emptyQueue();
      }
      return;
    }

    if (!this.filter.includes(type)) {
      return;
    }

    const body: PostPayload = {
      runtimeId: this.id,
      type,
      payload,
    };

    if (Boolean(this.isAuthRequired) && !Boolean(this.token)) {
      this.eventQueue.push(body);
      logger.info(
        `Backend runtime is waiting for the auth token. ${type} is pushed into an event queue`,
        type,
      );
      return;
    }

    void this.sendRequest(body);
  }

  authenticate(token: string): void {
    this.token = token;
  }

  async sendRequest(body: PostPayload): Promise<void> {
    let authHeaders = null;
    if (Boolean(this.isAuthRequired)) {
      if (this.token === undefined) {
        logErrorAndThrow('[sendRequest] App authorization token is required');
      }
      authHeaders = {
        Authorization: `Bearer ${this.token}`,
      };
    }

    try {
      const response = await fetch(this.url.href, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
        body: JSON.stringify(body),
      });

      // Check if the request was successful
      if (!response.ok) {
        logErrorAndThrow(
          `[sendRequest] Network response was not ok ${response.statusText}`,
        );
      }

      // Parse the JSON from the response

      const responseData: PostResponse = await response.json();
      this.handleMessage(responseData);
    } catch (error) {
      // Log any errors to the console
      logger.error('There was a problem with the fetch operation:', error);
    }
  }

  emptyQueue(): void {
    for (const body of this.eventQueue) {
      void this.sendRequest(body);
    }
    this.eventQueue = [];
  }

  private handleMessage(data: PostResponse): void {
    const spec = Specs.byActionType(data.type);
    if (!spec) {
      logger.warn(`Ignoring invalid capability action: ${data.type}`);
      return;
    }

    if (!this.instance.usesCapability(spec.id)) {
      logger.warn(`Rejecting action for unregistered capability: ${spec.id}`);
      return;
    }

    this.instance.receive([
      data.type,
      data.payload,
    ] as Constraints['actionPayloadTuple']);
  }
}
